import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { getAvatarBg, getInitials } from '@/components/ui/InstallerPicker';

interface InstallerToggleDropdownProps {
  userMap: Map<number, string>;
  categoryMap: Map<number, string>;
  selectedIds: number[];
  onToggle: (id: number) => void;
  onClose: () => void;
  projectName?: string;
  fixedPos?: { top?: number; bottom?: number; left: number; maxHeight: number };
}

const CATEGORY_ORDER = ['td', 'onderaannemer', 'easykit'] as const;
const CATEGORY_LABELS: Record<string, string> = {
  td: 'TD Installateurs',
  onderaannemer: 'Onderaannemers',
  easykit: 'Easykit',
};

export function InstallerToggleDropdown({
  userMap,
  categoryMap,
  selectedIds,
  onToggle,
  onClose,
  projectName,
  fixedPos,
}: InstallerToggleDropdownProps) {
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragOffset, setDragOffset] = useState({ dx: 0, dy: 0 });
  const dragRef = useRef<{ startX: number; startY: number; startDx: number; startDy: number } | null>(null);
  const [adjustedLeft, setAdjustedLeft] = useState<number | undefined>(fixedPos?.left);

  useEffect(() => {
    inputRef.current?.focus();
    if (fixedPos?.left == null || !containerRef.current) return;
    setAdjustedLeft(fixedPos.left);
    const rect = containerRef.current.getBoundingClientRect();
    const overflow = rect.right - window.innerWidth + 8;
    if (overflow > 0) setAdjustedLeft(fixedPos.left - overflow);
  }, [fixedPos?.left]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, startDx: dragOffset.dx, startDy: dragOffset.dy };

    function onMove(ev: MouseEvent) {
      if (!dragRef.current) return;
      setDragOffset({
        dx: dragRef.current.startDx + (ev.clientX - dragRef.current.startX),
        dy: dragRef.current.startDy + (ev.clientY - dragRef.current.startY),
      });
    }
    function onUp() {
      dragRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [dragOffset.dx, dragOffset.dy]);

  const groups = useMemo(() => {
    const term = search.toLowerCase();
    const buckets = new Map<string, [number, string][]>();
    for (const cat of CATEGORY_ORDER) buckets.set(cat, []);

    for (const [id, name] of userMap) {
      if (id === 0) continue;
      if (term && !name.toLowerCase().includes(term)) continue;
      const cat = categoryMap.get(id) || 'td';
      const bucket = buckets.get(cat);
      if (bucket) bucket.push([id, name]);
      else buckets.set(cat, [[id, name]]);
    }

    for (const bucket of buckets.values()) {
      bucket.sort((a, b) => a[1].localeCompare(b[1], 'nl'));
    }
    return buckets;
  }, [userMap, categoryMap, search]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const hasResults = [...groups.values()].some((g) => g.length > 0);

  function renderRow(id: number, name: string) {
    const isSelected = selectedSet.has(id);
    return (
      <button
        key={id}
        onClick={() => onToggle(id)}
        className={`flex items-center gap-2 w-full text-left px-2 py-1.5 text-sm rounded-lg transition-colors ${
          isSelected ? 'bg-white/15 text-white' : 'text-white/80 hover:bg-white/10'
        }`}
      >
        <span className={`w-6 h-6 rounded-full ${getAvatarBg(id)} flex items-center justify-center text-[9px] font-bold text-white shrink-0`}>
          {getInitials(name)}
        </span>
        <span className="truncate flex-1 text-[13px]">{name}</span>
        {isSelected && (
          <svg className="w-4 h-4 text-green-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </button>
    );
  }

  const visibleGroups = CATEGORY_ORDER.filter((cat) => (groups.get(cat)?.length || 0) > 0);

  return (
    <div
      ref={containerRef}
      className={`${fixedPos ? 'fixed z-50' : 'absolute left-0 top-full mt-1 z-30'} bg-td-navy rounded-xl shadow-xl py-2 w-max max-w-[90vw] flex flex-col`}
      style={fixedPos ? {
        top: fixedPos.top,
        bottom: fixedPos.bottom,
        left: adjustedLeft,
        maxHeight: fixedPos.maxHeight,
        transform: `translate(${dragOffset.dx}px, ${dragOffset.dy}px)`,
      } : undefined}
    >
      <div
        className="flex items-center gap-2 cursor-grab active:cursor-grabbing px-3 pb-1.5 pt-1 select-none border-b border-white/10 mb-1"
        onMouseDown={handleDragStart}
      >
        <svg className="w-3.5 h-3.5 text-white/30 shrink-0" fill="currentColor" viewBox="0 0 24 24">
          <circle cx="9" cy="5" r="1.5"/><circle cx="15" cy="5" r="1.5"/>
          <circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/>
          <circle cx="9" cy="19" r="1.5"/><circle cx="15" cy="19" r="1.5"/>
        </svg>
        {projectName && (
          <p className="text-xs font-semibold text-white/70 truncate">{projectName}</p>
        )}
      </div>
      <div className="px-3 pb-2">
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Zoek installateur..."
          className="w-full px-2.5 py-1.5 text-sm bg-white/10 text-white placeholder-white/40 rounded-lg border border-white/20 focus:outline-none focus:border-white/40"
        />
      </div>
      {!hasResults && (
        <p className="px-3 py-2 text-sm text-white/40">Geen resultaten</p>
      )}
      <div className="flex gap-0 px-1 overflow-y-auto min-h-0">
        {visibleGroups.map((cat, idx) => {
          const items = groups.get(cat) || [];
          return (
            <div key={cat} className={`min-w-[160px] px-2 ${idx > 0 ? 'border-l border-white/10' : ''}`}>
              <p className="px-2 pt-1 pb-1.5 text-[10px] font-semibold text-white/40 uppercase tracking-wider">
                {CATEGORY_LABELS[cat] || cat}
              </p>
              {items.map(([id, name]) => renderRow(id, name))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
